import {useMutation} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {CREATE_MUTUAL_FUND} from 'graphql/mutations/assetManagement';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {handleClearMutualFundsState} from 'redux/store/assetClass';
import {currencySymbols} from 'utils/mock';

export const CreateMutualFundsModal = ({handleClose}) => {
  const {assetId: assetKey} = useParams();
  const mutualFundsData = useSelector(state => state?.assetClass);
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const {
    description,
    language,
    topics,
    documents,
    tradeable,
    feeSchedule,
    issuer,
    forDabaPro,
    enabledForFreeSimulation,
    issuerType,
    currency,
    name,
    endDate,
    useInventoryForTrade,
    groupId,
    sectorIndustry,
    exchange,
    invitedUsers,
    icon,
    invitedGroups,
    listingType,
    shariaCompliant,
    fractionalShares,
    lotSize,
    minTradeableUnits,
    maxTradeableUnits,
    startDate,
    orderExpirationLimit,
    marketPhase,
    riskLevel,
    keyFacts,
    disclosures,
    settings,
  } = mutualFundsData?.mutualFundsInstruments;

  const [createMutualFund, {loading}] = useMutation(CREATE_MUTUAL_FUND, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  // The step-two screens all dispatch into one `mutualFunds` slice, but their
  // fields belong to two different inputs. Spreading the slice wholesale sent
  // ticker/isin/countries to MutualFundInput, which rejects them. Partition by
  // actual input membership instead.
  //
  // MutualFundInput, introspected — everything else in the slice is either a
  // base-instrument field or not writable at all.
  const FUND_INPUT_KEYS = [
    'currentNetAssetValue',
    'netAssetValueAtInception',
    'currentNavDate',
    'benefits',
    'bestSuitedFor',
    'projectedAnnualReturn',
    'type',
    'inceptionDate',
    'investmentStrategy',
    'investmentHorizon',
    'benchmark',
    'managedBy',
    'assetAllocation',
    'settlementDaysOnRedemption',
    'allowsAutoInvest',
    'valuationFrequency',
  ];

  // Step-two screens also collect base-instrument fields — ticker, isin and
  // countries are entered on the Overview screen but belong to the base input.
  // Without this they were dropped entirely, and the backend needs `ticker` to
  // derive the `symbol` on MutualFundValuationHistory.
  const BASE_INPUT_KEYS_FROM_STEP_TWO = [
    'ticker',
    'isin',
    'countries',
    'returns',
  ];

  const handleCreateMutualFund = () => {
    // Field set matches MutualFundInput / BaseFinancialInstrumentInput.
    // Removed by the migration: dabaScore, pitchDeck, faq, missionAndVision,
    // solutions, liquidity, investmentRisk, keyStatistics, riskAndEsg,
    // impactAndEsg, additionalDisclosures, additionalInvestmentTerms,
    // recommendedPortfolioStrategy, disclosure, externallyFundedAmountBreakDown.
    // Funds have no offering, so useOfProceeds is gone too.
    const slice = mutualFundsData?.mutualFunds || {};

    // Fund-level fields only.
    const fundLevel = Object.fromEntries(
      Object.entries(slice).filter(
        ([key, value]) => FUND_INPUT_KEYS.includes(key) && value !== '',
      ),
    );

    // Base-instrument fields the step-two screens nested under their own key
    // (currently `returns` from the performance screen).
    const nestedBase = slice.baseFinancialInstrumentInput || {};

    // …and the ones they left flat.
    const baseFromStepTwo = Object.fromEntries(
      Object.entries(slice).filter(
        ([key, value]) =>
          BASE_INPUT_KEYS_FROM_STEP_TWO.includes(key) && value !== '',
      ),
    );

    createMutualFund({
      variables: {
        input: {
          ...fundLevel,
          baseFinancialInstrumentInput: {
            ...nestedBase,
            ...baseFromStepTwo,
            key: 'MUTUAL_FUND',
            assetClassKey: assetKey,
            name,
            description,
            icon,
            language,
            topics,
            keyFacts,
            disclosures,
            documents,
            settings,
            groupId,
            sectorIndustry,
            exchange,
            invitedUsers,
            invitedGroups,
            listingType,
            marketPhase,
            riskLevel,
            tradeable,
            useInventoryForTrade,
            shariaCompliant,
            forDabaPro,
            enabledForFreeSimulation,
            fractionalShares,
            lotSize,
            minTradeableUnits,
            maxTradeableUnits,
            orderExpirationLimit,
            ...(startDate && {startDate: moment(startDate).format()}),
            ...(endDate && {endDate: moment(endDate).format()}),
            issuer: {
              id: issuer,
              issuerType,
            },
            currency: {
              name: currencySymbols[currency]?.name,
              symbol: currencySymbols[currency]?.symbol,
              currencyCode: currencySymbols[currency]?.value,
            },
            // An empty fee schedule must be omitted, not sent as `false`.
            ...(Array.isArray(feeSchedule) &&
              feeSchedule.length && {
                feeSchedule: feeSchedule.map(data => ({
                  feeName: data?.feeName,
                  feeType: data?.feeType,
                  ...(data?.fixedFeeAmount && {
                    fixedFeeAmount: Number(data?.fixedFeeAmount),
                  }),
                  ...(data.feeType === 'ENTRY' && {
                    level: data?.level,
                    // Sent explicitly, not spread conditionally: the whole schedule is
                    // overwritten on save, so an omitted false reads as "unchanged"
                    // against a previously-true value and the toggle could never be
                    // turned back off.
                    perExchange: Boolean(data?.perExchange),
                  }),
                  ...(data?.variableFeeAmount && {
                    variableFeeAmount: Number(data?.variableFeeAmount),
                  }),
                  scalable: data?.scalable ?? false,
                  ...(data?.feeType === 'RECURRING' && {
                    frequency: data?.frequency,
                    origination: data?.origination,
                  }),
                  ...(data?.feeType === 'EXIT' &&
                    Array.isArray(data?.vector) && {
                      vector: data?.vector?.map(vect => ({
                        duration: vect?.duration,
                        magnitude: vect?.magnitude,
                      })),
                    }),
                })),
              }),
          },
        },
      },
    })
      .then(({data: {createMutualFund}}) => {
        if (
          createMutualFund?.__typename === 'Error' ||
          createMutualFund?.statusCode === 400
        ) {
          toast.error(createMutualFund?.message);
        } else {
          toast.success('Mutual Fund created successfully');
          handleClose();
          dispatch(handleClearMutualFundsState());
          navigate(`/dashboard/asset-management/class/FUNDS/instruments`);
        }
      })
      .catch(error => toast.error(error.message));
  };

  return (
    <div className="confirm-modal">
      <div>
        <h1>Create Mutual Fund</h1>
        <p>
          Please make sure the details are correctly inputted before proceeding
        </p>
      </div>
      <div className="buttons">
        <Button
          disabled={loading}
          label={!loading ? 'Create' : 'Loading...'}
          onClick={handleCreateMutualFund}
        />
        <Button type="secondary" label="No" onClick={handleClose} />
      </div>
    </div>
  );
};
